<script setup lang="ts">
import type { FormKitFrameworkContext, FormKitNode } from '@formkit/core'
import { useMutation, useQuery } from '@urql/vue'
import {
  CreateTranslationSuggestionDocument,
  TranslationSettingsDocument,
  TranslatableEntityType,
  TranslationMode,
  type TranslatedField,
  type TranslationResult,
  type TranslationStaleStatus,
} from '~/graphql/operations'
import AiFillButton from '~/components/translation/AiFillButton.vue'

const props = defineProps<{ context: FormKitFrameworkContext }>()

const attrProps = computed<
  {
    entityType: TranslatableEntityType
    entityId?: number | null
    sourceLanguageId?: number | null
    fields: string[]
    sourceChannelId?: number | null
    targetChannelId?: number | null
  } & AiFillCollectionOptions
>(() => (props.context.attrs?.props as any) || {})

const store = useStore()

const canReadStale = computed(() =>
  (store.auth?.user?.permissions || []).some(
    (p) =>
      p === 'translationStale' ||
      p.startsWith('translationStale:') ||
      p === '**:w' ||
      p === '**',
  ),
)

const groupNode = computed<FormKitNode | undefined>(
  () => props.context.node.parent || undefined,
)

const targetLanguageId = computed<number | null>(() => {
  const value: any = groupNode.value?.value
  const id = value?.languageId
  return typeof id === 'number' ? id : id ? +id : null
})

const isTargetTab = computed(
  () =>
    !!attrProps.value.sourceLanguageId &&
    !!targetLanguageId.value &&
    attrProps.value.sourceLanguageId !== targetLanguageId.value,
)

const isCollection = computed(() => !!attrProps.value.collectionKey)
const collectionChildId = useCollectionChildId(props.context.node, attrProps)

const stale = useTranslationStaleStatus()

const status = computed<TranslationStaleStatus | undefined>(() => {
  if (
    !isTargetTab.value ||
    isCollection.value ||
    !canReadStale.value ||
    !attrProps.value.entityId ||
    !attrProps.value.sourceLanguageId ||
    !targetLanguageId.value
  ) {
    return undefined
  }
  return stale.getCached({
    entityType: attrProps.value.entityType || TranslatableEntityType.Module,
    entityId: attrProps.value.entityId,
    sourceLanguageId: attrProps.value.sourceLanguageId,
    targetLanguageId: targetLanguageId.value,
    sourceChannelId: attrProps.value.sourceChannelId ?? null,
    targetChannelId: attrProps.value.targetChannelId ?? null,
  })
})

watch(
  [
    () => attrProps.value.entityId,
    () => attrProps.value.sourceLanguageId,
    targetLanguageId,
  ],
  ([entityId, sourceLanguageId, targetId]) => {
    if (
      !canReadStale.value ||
      isCollection.value ||
      !entityId ||
      !sourceLanguageId ||
      !targetId ||
      sourceLanguageId === targetId
    ) {
      return
    }
    if (status.value) return
    stale
      .fetchStatus({
        entityType: attrProps.value.entityType || TranslatableEntityType.Module,
        entityId,
        sourceLanguageId,
        targetLanguageId: targetId,
        sourceChannelId: attrProps.value.sourceChannelId ?? null,
        targetChannelId: attrProps.value.targetChannelId ?? null,
      })
      .catch(() => {})
  },
  { immediate: true },
)

const languageName = (id?: number | null) => {
  if (!id) return null
  return (store.languages || []).find((l) => +l.id === id)?.name || `#${id}`
}
const sourceLanguageName = computed(() =>
  languageName(attrProps.value.sourceLanguageId),
)
const targetLanguageName = computed(() => languageName(targetLanguageId.value))

const formatWhen = (raw: string | null | undefined) => {
  if (!raw) return ''
  try {
    return new Date(raw).toLocaleString()
  } catch {
    return String(raw)
  }
}

const daysAgo = (raw: string | null | undefined): number | null => {
  if (!raw) return null
  const then = new Date(raw).getTime()
  if (!Number.isFinite(then)) return null
  const diffMs = Date.now() - then
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)))
}
const sourceUpdatedDaysAgo = computed(() =>
  daysAgo(status.value?.sourceUpdatedAt),
)
const targetUpdatedDaysAgo = computed(() =>
  daysAgo(status.value?.targetUpdatedAt),
)

const dismissKey = computed(() => {
  if (
    !attrProps.value.entityId ||
    !attrProps.value.sourceLanguageId ||
    !targetLanguageId.value
  )
    return null
  return `translation:staleDismiss:${attrProps.value.entityType}:${attrProps.value.entityId}:${attrProps.value.sourceLanguageId}:${targetLanguageId.value}`
})
const dismissedFor = ref<string | null>(null)
watch(
  [
    dismissKey,
    () => status.value?.sourceUpdatedAt,
    () => status.value?.targetUpdatedAt,
  ],
  ([key]) => {
    if (typeof window === 'undefined' || !key) {
      dismissedFor.value = null
      return
    }
    dismissedFor.value = sessionStorage.getItem(key)
  },
  { immediate: true },
)
const staleSignature = computed(
  () =>
    `${status.value?.sourceUpdatedAt ?? ''}|${status.value?.targetUpdatedAt ?? ''}`,
)
const isStaleBannerDismissed = computed(
  () => !!dismissedFor.value && dismissedFor.value === staleSignature.value,
)
const dismissStaleBanner = () => {
  if (typeof window === 'undefined' || !dismissKey.value) return
  sessionStorage.setItem(dismissKey.value, staleSignature.value)
  dismissedFor.value = staleSignature.value
}

const targetIsAiTranslated = computed(
  () => status.value?.isTargetMachineTranslated === true,
)
const showStaleTip = computed(
  () =>
    isTargetTab.value &&
    !!status.value?.isStale &&
    targetIsAiTranslated.value &&
    !isStaleBannerDismissed.value,
)

const canCreateDraft = computed(() =>
  (store.auth?.user?.permissions || []).some(
    (p) => p === 'translationInbox:w' || p === '**:w' || p === '**',
  ),
)
const createDraftMutation = useMutation(CreateTranslationSuggestionDocument)
const router = useRouter()
const draftSubmitting = ref(false)

const onCreateDraft = async () => {
  if (
    !canCreateDraft.value ||
    !attrProps.value.entityId ||
    !attrProps.value.sourceLanguageId ||
    !targetLanguageId.value
  )
    return
  draftSubmitting.value = true
  try {
    const res = await createDraftMutation.executeMutation({
      input: {
        entityType: attrProps.value.entityType || TranslatableEntityType.Module,
        entityId: attrProps.value.entityId,
        sourceLanguageId: attrProps.value.sourceLanguageId,
        targetLanguageId: targetLanguageId.value,
        sourceChannelId: attrProps.value.sourceChannelId ?? null,
        targetChannelId: attrProps.value.targetChannelId ?? null,
        fields: attrProps.value.fields || [],
        mode: TranslationMode.Draft,
      },
    })
    if (res.error) throw res.error
    const id = res.data?.createTranslationSuggestion?.id
    if (id) {
      notify({ title: 'Draft created', type: 'success' })
      router.push(`/translations/review/${id}`)
    }
  } catch (e: any) {
    notify({
      title: 'Failed to create draft',
      type: 'error',
      text: e?.message || 'Unknown error',
    })
  } finally {
    draftSubmitting.value = false
  }
}

const staleFieldList = computed(() => status.value?.sourceMissingFields || [])

const fillLabel = computed(() => {
  if (isCollection.value) {
    return sourceLanguageName.value
      ? `Translate from ${sourceLanguageName.value}`
      : 'AI fill from source'
  }
  if (status.value?.isMissing) {
    return sourceLanguageName.value
      ? `Translate from ${sourceLanguageName.value}`
      : 'Translate from source'
  }
  if (status.value?.isStale && targetIsAiTranslated.value) {
    const count = staleFieldList.value.length
    if (count > 0) {
      return `Re-translate ${count} stale field${count === 1 ? '' : 's'}`
    }
    return 'Re-translate this tab'
  }
  return 'AI fill from source'
})

const buttonFields = computed(() => {
  const fields = attrProps.value.fields || []
  if (!isCollection.value) return fields
  if (!collectionChildId.value) return []
  return fields.map((field) =>
    encodeCollectionField(
      attrProps.value.collectionKey!,
      collectionChildId.value!,
      field,
    ),
  )
})

const fillFields = computed(() => {
  // When source tracks per-field stale state, scope the button to only the stale ones.
  if (status.value?.isStale && staleFieldList.value.length > 0) {
    const allFields = attrProps.value.fields || []
    return staleFieldList.value.filter((name) => allFields.includes(name))
  }
  return attrProps.value.fields || []
})

const lastWarnings = ref<string[]>([])
const lastGlossaryTerms = ref<string[]>([])
const fieldWarnings = ref<Record<string, string[]>>({})
const lastResultMeta = ref<{
  model: string
  costCents: number
  isEstimate: boolean
} | null>(null)

const settingsQuery = useQuery({
  query: TranslationSettingsDocument,
  variables: {},
  requestPolicy: 'cache-first',
})
const inlineBypassAllowed = computed(
  () =>
    settingsQuery.data.value?.translationSettings
      ?.canInlineAiFillBypassApproval ?? true,
)
const pendingInlineAiFill = ref(false)
const needsApprovalWarning = computed(
  () =>
    isTargetTab.value &&
    !inlineBypassAllowed.value &&
    pendingInlineAiFill.value,
)

watch(targetLanguageId, () => {
  pendingInlineAiFill.value = false
})

const formatEuros = (cents: number) =>
  cents > 0 ? `€${(cents / 100).toFixed(4)}` : '€0'

const findChildByName = (
  node: FormKitNode,
  name: string,
): FormKitNode | undefined => {
  // node.at(name) starts traversal at node.parent, so a sibling-style lookup
  // misses the group's own children. Walk children directly + recurse one
  // level to also reach simple flex-wrapper $el groups in the schema.
  const direct = node.children.find(
    (c: any) => !('__FKP' in c) && String(c.name) === name,
  )
  if (direct) return direct as FormKitNode
  for (const c of node.children as any[]) {
    if ('__FKP' in c) continue
    const nested = (c.children || []).find(
      (cc: any) => !('__FKP' in cc) && String(cc.name) === name,
    )
    if (nested) return nested
  }
  return undefined
}

const applyTranslatedFields = (fields: TranslatedField[]) => {
  const node = groupNode.value
  if (!node) return
  const requested = new Set(buttonFields.value)
  const nextFieldWarnings: Record<string, string[]> = {}
  for (const f of fields) {
    // In collection mode the API may echo fields of other children — only this
    // child's fields belong in this tab.
    if (isCollection.value && !requested.has(f.name)) continue
    const fieldName = isCollection.value
      ? decodeCollectionField(f.name)
      : f.name
    if (f.value != null) {
      const child = findChildByName(node, fieldName)
      if (child) child.input(f.value, false)
    }
    if (f.warnings?.length) nextFieldWarnings[fieldName] = f.warnings
  }
  fieldWarnings.value = nextFieldWarnings
}

const onFilled = (payload: {
  translatedFields: TranslatedField[]
  result: TranslationResult
}) => {
  applyTranslatedFields(payload.translatedFields)
  pendingInlineAiFill.value = true
  lastWarnings.value = payload.result.warnings || []
  lastGlossaryTerms.value = payload.result.glossaryTermsApplied || []
  lastResultMeta.value = {
    model: payload.result.model,
    costCents: payload.result.costCents,
    isEstimate: payload.result.isEstimate,
  }
  if (lastGlossaryTerms.value.length) {
    notify({
      title: 'AI fill complete',
      type: 'success',
      text: `Applied ${lastGlossaryTerms.value.length} glossary term(s): ${lastGlossaryTerms.value.join(', ')}`,
    })
  }
  // Optimistically clear local staleness — DB will refresh on next save.
  if (
    !isCollection.value &&
    attrProps.value.entityId &&
    attrProps.value.sourceLanguageId &&
    targetLanguageId.value
  ) {
    stale.invalidate({
      entityType: attrProps.value.entityType || TranslatableEntityType.Module,
      entityId: attrProps.value.entityId,
      sourceLanguageId: attrProps.value.sourceLanguageId,
      targetLanguageId: targetLanguageId.value,
      sourceChannelId: attrProps.value.sourceChannelId ?? null,
      targetChannelId: attrProps.value.targetChannelId ?? null,
    })
  }
}
</script>

<template>
  <div
    v-if="!isCollection || isTargetTab"
    class="ai-fill-slot border-b border-slate-100 pb-3 mb-1 space-y-2"
  >
    <!-- Stale / missing banner -->
    <div
      v-if="isTargetTab && status?.isMissing"
      class="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900"
    >
      <i class="i-icon-park-outline:info text-lg shrink-0 mt-0.5" />
      <div class="flex-1">
        <div>
          No translation yet for this language<span v-if="sourceLanguageName">
            — source content available in
            <strong>{{ sourceLanguageName }}</strong></span
          >.
        </div>
      </div>
    </div>
    <div
      v-else-if="showStaleTip"
      class="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900"
    >
      <i class="i-icon-park-outline:attention text-lg shrink-0 mt-0.5" />
      <div class="flex-1">
        <div>
          <strong>
            <span v-if="sourceLanguageName && targetLanguageName">
              {{ targetLanguageName }} translation is older than
              {{ sourceLanguageName }} source.
            </span>
            <span v-else>This translation is older than the source.</span>
          </strong>
        </div>
        <div class="text-xs mt-0.5">
          Source updated
          <span v-if="sourceUpdatedDaysAgo !== null"
            >{{ sourceUpdatedDaysAgo }}d ago</span
          ><span v-else>{{ formatWhen(status.sourceUpdatedAt) }}</span>
          · target last updated
          <span v-if="targetUpdatedDaysAgo !== null"
            >{{ targetUpdatedDaysAgo }}d ago</span
          ><span v-else>{{ formatWhen(status.targetUpdatedAt) }}</span>
        </div>
      </div>
      <button
        type="button"
        class="text-amber-700 hover:text-amber-900 p-1 -mr-1 -mt-1"
        title="Dismiss until source changes again"
        @click="dismissStaleBanner"
      >
        <i class="i-icon-park-outline:close text-base" />
      </button>
    </div>

    <!-- Bypass-approval warning -->
    <div
      v-if="needsApprovalWarning"
      class="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-900"
    >
      <i class="i-icon-park-outline:caution text-lg shrink-0 mt-0.5" />
      <div class="flex-1">
        <div>
          <strong>Approval required.</strong> Saving an AI fill in this tab will
          create a draft for editorial review instead of publishing directly.
        </div>
      </div>
    </div>

    <!-- AI fill button + draft entry point -->
    <div
      v-if="
        attrProps.entityId &&
        targetLanguageId &&
        (!isCollection || (isTargetTab && collectionChildId))
      "
      class="flex items-center gap-2 flex-wrap"
    >
      <AiFillButton
        :entity-type="attrProps.entityType || TranslatableEntityType.Module"
        :entity-id="attrProps.entityId"
        :target-language-id="targetLanguageId"
        :source-language-id="attrProps.sourceLanguageId ?? null"
        :source-channel-id="attrProps.sourceChannelId ?? null"
        :target-channel-id="attrProps.targetChannelId ?? null"
        :fields="buttonFields"
        :label="fillLabel"
        @filled="onFilled"
      />
      <button
        v-if="!isCollection && canCreateDraft && attrProps.sourceLanguageId"
        type="button"
        :disabled="draftSubmitting"
        class="inline-flex items-center gap-1.5 rounded border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/40 disabled:opacity-50"
        title="Create a draft suggestion for editorial review (does not patch this form)"
        @click="onCreateDraft"
      >
        <i
          class="text-sm"
          :class="
            draftSubmitting
              ? 'i-icon-park-outline:loading-one animate-spin'
              : 'i-icon-park-outline:doc-detail'
          "
        />
        Generate AI draft for review
      </button>
    </div>
    <p v-else class="text-xs text-slate-400 italic">
      Save the module first to enable AI fill.
    </p>

    <div
      v-if="lastResultMeta"
      class="text-xs text-slate-500 flex flex-wrap items-center gap-3"
    >
      <span class="inline-flex items-center gap-1">
        <i class="i-icon-park-outline:check-one text-emerald-600 text-sm" />
        Filled via
        <span class="font-mono text-slate-700">{{ lastResultMeta.model }}</span>
      </span>
      <span class="text-slate-400">·</span>
      <span>
        {{ lastResultMeta.isEstimate ? 'est.' : '' }}
        {{ formatEuros(lastResultMeta.costCents) }}
      </span>
    </div>

    <div
      v-if="lastWarnings.length"
      class="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
    >
      <strong>Warnings:</strong>
      <ul class="list-disc pl-4">
        <li v-for="(w, idx) in lastWarnings" :key="idx">{{ w }}</li>
      </ul>
    </div>

    <div
      v-for="(warns, field) in fieldWarnings"
      :key="field"
      class="text-xs text-amber-700"
    >
      <strong>{{ field }}:</strong> {{ warns.join(' • ') }}
    </div>
  </div>
</template>
